// pages/user-dashboard.ts — applicant's home screen.
//
// Mirrors frontend2/User.jsx :: UserDashboard:
//   • Big greeting + auth-aware subline + "Start a new application" CTA
//   • "Action Required" alert at the top if any grant is in that state
//   • 4 stat tiles (approved $ all time, in review, drafts, approval rate)
//   • "In progress" list of ongoing-status grants (status, progress bar,
//     amount, Resume/View button)
//   • "Past applications" ledger table (Approved + Denied), click-to-detail

import type { Page, PageContext } from '../router';
import {
  html, esc, arrowR, firstNameOf,
  fmtMoney, fmtMoneyOrDash, fmtDate, fmtRelative,
  statusPillClass,
} from '../util';

interface Grant {
  GrantId: number;
  Status: string;
  ProjectName: string;
  RequestedAmount: number | null;
  ApprovedAmount: number | null;
  ReferenceNumber?: string;
  SubmittedAt?: string;
  ReviewedAt?: string;
  UpdatedAt?: string;
}

const ONGOING_STATUSES = ['Draft', 'Submitted', 'Under Review', 'Action Required'];
const FINAL_STATUSES   = ['Approved', 'Denied'];

function stepOf(status: string): number {
  if (status === 'Draft') return 1;
  if (status === 'Approved' || status === 'Denied') return 3;
  return 2;
}
function yearOf(iso: string | null | undefined): string | number {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return Number.isFinite(d.getFullYear()) ? d.getFullYear() : '\u2014';
}

function renderStatTile(label: string, value: string, sub: string): string {
  return html`
    <div class="card" style="padding: 28px 28px;">
      <div class="label">${label}</div>
      <div class="serif" style="font-size: 38px; line-height: 1; color: var(--primary); margin-top: 12px;">${value}</div>
      ${{ __raw: sub ? html`<div class="t-small" style="margin-top: 12px; word-break: break-word;">${sub}</div>` : '' }}
    </div>
  `;
}

function renderOngoingCard(g: Grant): string {
  const step = stepOf(g.Status);
  const stepLabels = ['Identify', 'Upload', 'Submit'];
  const amount = Number(g.RequestedAmount || 0);
  const isDraft = g.Status === 'Draft';
  const action  = isDraft ? 'resume' : 'view';

  const bars = [1, 2, 3].map(n => `
    <div style="flex: 1; height: 4px; border-radius: 2px;
                background: ${n <= step ? 'var(--primary)' : 'var(--paper-3)'};"></div>
  `).join('');
  const labels = stepLabels.map((s, j) => `
    <span class="t-small" style="font-size: 11px; color: ${j < step ? 'var(--primary)' : 'var(--ink-mute)'};
          font-weight: ${j < step ? 500 : 400};">${s}</span>
  `).join('');

  return html`
    <article class="card ud-ongoing-card" data-grant-id="${g.GrantId}" data-action="${action}">
      <div>
        <div class="d-flex" style="gap: 12px; align-items: center; margin-bottom: 16px;">
          <span class="pill ${statusPillClass(g.Status)}">${g.Status}</span>
          ${{ __raw: g.ReferenceNumber ? html`<span class="t-small">Reference ${g.ReferenceNumber}</span>` : '' }}
        </div>
        <h3 class="t-h3 serif" style="margin: 0;">${g.ProjectName || 'Untitled draft'}</h3>
        <p class="t-small" style="margin-top: 12px;">Last updated ${fmtRelative(g.UpdatedAt)} · Step ${step} of 3</p>
        <div class="d-flex" style="gap: 8px; margin-top: 24px; max-width: 360px;">${{ __raw: bars }}</div>
        <div class="d-flex" style="margin-top: 12px; max-width: 360px; justify-content: space-between;">${{ __raw: labels }}</div>
      </div>
      <div>
        <div class="label">Requested</div>
        <div class="serif" style="font-size: 32px; color: var(--primary); line-height: 1; margin-top: 8px;">
          ${amount > 0 ? fmtMoney(amount) : '\u2014'}
        </div>
      </div>
      <div>
        <div class="label">Status</div>
        <div class="serif" style="font-size: 18px; line-height: 1.3; margin-top: 8px;">${g.Status}</div>
        ${{ __raw: g.SubmittedAt ? html`<div class="t-small" style="margin-top: 4px;">Submitted ${fmtRelative(g.SubmittedAt)}</div>` : '' }}
      </div>
      <a class="btn btn-primary" data-grant-id="${g.GrantId}" data-action="${action}">
        ${isDraft ? 'Resume' : 'View'} ${{ __raw: arrowR() }}
      </a>
    </article>
  `;
}

function renderHistoryRow(g: Grant): string {
  const amount = g.Status === 'Approved' ? g.ApprovedAmount : g.RequestedAmount;
  return html`
    <tr style="cursor: pointer;" data-grant-id="${g.GrantId}" data-action="view">
      <td><span class="serif" style="font-size: 20px;">${g.ProjectName}</span></td>
      <td class="num">${yearOf(g.ReviewedAt || g.SubmittedAt || g.UpdatedAt)}</td>
      <td><span class="serif" style="font-size: 17px; color: var(--primary);">${fmtMoney(Number(amount || 0))}</span></td>
      <td><span class="pill ${statusPillClass(g.Status)}">${g.Status}</span></td>
      <td><span style="color: var(--ink-mute);">→</span></td>
    </tr>
  `;
}

const UserDashboard: Page = {
  guard(ctx: PageContext): string | null {
    return ctx.user ? null : 'login';
  },

  render(ctx: PageContext): string {
    const user = ctx.user as { fullName?: string; FullName?: string; email?: string; Email?: string } | null;
    const name  = user?.fullName || user?.FullName || '';
    const email = user?.email    || user?.Email    || '';
    const greeting = `Good day, ${firstNameOf(name)}.`;
    return html`
      <section style="padding: 56px 0 16px;">
        <div class="wrap">
          <div class="t-small">${name} · ${email}</div>
          <div class="d-flex ud-head" style="justify-content: space-between; align-items: end; flex-wrap: wrap; gap: 24px; margin-top: 24px;">
            <div>
              <h1 class="t-h1 ud-greeting" style="margin: 0;">${greeting}</h1>
              <p class="t-lede" id="ud-subline" style="margin: 16px 0 0;">Loading your applications…</p>
            </div>
            <a class="btn btn-primary" data-nav="grant-form">Start a new application ${{ __raw: arrowR() }}</a>
          </div>
          <div id="ud-action-banner"></div>
        </div>
      </section>

      <section style="padding: 32px 0;">
        <div class="wrap">
          <div id="ud-stat-tiles" class="ud-stat-grid">
            ${{ __raw: [1, 2, 3, 4].map(() => renderStatTile('', '—', '')).join('') }}
          </div>
        </div>
      </section>

      <section style="padding: 48px 0 24px;">
        <div class="wrap">
          <div class="d-flex" style="justify-content: space-between; align-items: baseline; margin-bottom: 24px;">
            <h2 class="t-h2" style="margin: 0; font-size: 36px;">In progress</h2>
            <div class="t-small" id="ud-ongoing-count"></div>
          </div>
          <div id="ud-ongoing"><p class="t-body">Loading…</p></div>
        </div>
      </section>

      <section id="ud-history-section" style="padding: 48px 0 96px; display: none;">
        <div class="wrap">
          <div class="d-flex" style="justify-content: space-between; align-items: baseline; margin-bottom: 8px;">
            <h2 class="t-h2" style="margin: 0; font-size: 36px;">Past applications</h2>
            <div class="t-small" id="ud-history-count"></div>
          </div>
          <div id="ud-history"></div>
        </div>
      </section>
    `;
  },

  mount(root: HTMLElement, ctx: PageContext): void {
    let alive = true;
    const sublineEl   = root.querySelector('#ud-subline')        as HTMLElement | null;
    const bannerEl    = root.querySelector('#ud-action-banner')  as HTMLElement | null;
    const tilesEl     = root.querySelector('#ud-stat-tiles')     as HTMLElement | null;
    const ongoingEl   = root.querySelector('#ud-ongoing')        as HTMLElement | null;
    const ongoingCt   = root.querySelector('#ud-ongoing-count')  as HTMLElement | null;
    const historySect = root.querySelector('#ud-history-section') as HTMLElement | null;
    const historyEl   = root.querySelector('#ud-history')        as HTMLElement | null;
    const historyCt   = root.querySelector('#ud-history-count')  as HTMLElement | null;

    function fail(msg: string): void {
      if (sublineEl) sublineEl.textContent = '';
      if (ongoingEl) {
        ongoingEl.innerHTML =
          `<p class="t-body" style="color: var(--st-denied);">Could not load grants (${esc(msg)}).</p>`;
      }
    }

    // Delegated click handlers — any [data-grant-id][data-action]
    // navigates to the appropriate page. Used by ongoing cards, the
    // resume/view button on each, and past-application rows.
    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      const el = t?.closest('[data-grant-id][data-action]') as HTMLElement | null;
      if (!el) return;
      e.preventDefault();
      const grantId = Number(el.getAttribute('data-grant-id') || 0);
      const action  = el.getAttribute('data-action') || 'view';
      if (action === 'resume') {
        ctx.navigate('grant-form',   { id: grantId });
      } else {
        ctx.navigate('grant-detail', { id: grantId });
      }
    });

    window.api.listGrants()
      .then(rows => {
        if (!alive) return;
        const grants = (Array.isArray(rows) ? rows : []) as Grant[];

        const ongoing = grants.filter(g => ONGOING_STATUSES.includes(g.Status));
        const history = grants
          .filter(g => FINAL_STATUSES.includes(g.Status))
          .sort((a, b) =>
            new Date(b.ReviewedAt || b.UpdatedAt || 0).getTime()
            - new Date(a.ReviewedAt || a.UpdatedAt || 0).getTime());

        const drafts        = ongoing.filter(g => g.Status === 'Draft').length;
        const inReview      = ongoing.filter(g => g.Status !== 'Draft').length;
        const approvedAll   = grants.filter(g => g.Status === 'Approved');
        const approvedTotal = approvedAll.reduce((s, g) => s + Number(g.ApprovedAmount || 0), 0);
        const decided       = history.length;
        const approvalRate  = decided > 0 ? Math.round((approvedAll.length / decided) * 100) : null;
        const actionItem    = ongoing.find(g => g.Status === 'Action Required');
        const draftHint     = ongoing.find(g => g.Status === 'Draft')?.ProjectName
                          || (drafts === 0 ? 'none open' : '');

        // Subline
        if (sublineEl) {
          sublineEl.textContent = grants.length === 0
            ? "You haven't applied for a grant yet."
            : `You have ${drafts} draft${drafts === 1 ? '' : 's'} in progress and ${decided} previous grant${decided === 1 ? '' : 's'} in your file.`;
        }

        // Action-required banner
        if (bannerEl && actionItem) {
          bannerEl.innerHTML = `
            <div class="alert-warn" style="margin-top: 24px; background: var(--accent-soft);
                 border-left: 3px solid var(--accent); color: var(--ink-2); border-radius: 10px;
                 padding: 14px 18px; display: flex; gap: 16px; align-items: flex-start;
                 justify-content: space-between; flex-wrap: wrap;">
              <div><strong>${esc(actionItem.ProjectName)}</strong> — a reviewer has requested additional information.</div>
              <a class="btn btn-primary btn-sm" data-grant-id="${actionItem.GrantId}" data-action="view">Respond now →</a>
            </div>
          `;
        }

        // Stat tiles
        if (tilesEl) {
          tilesEl.innerHTML = [
            renderStatTile('Approved · all time',
                           approvedAll.length > 0 ? fmtMoney(approvedTotal) : '\u2014',
                           approvedAll.length + ' grant' + (approvedAll.length === 1 ? '' : 's')),
            renderStatTile('Currently in review',
                           inReview > 0 ? String(inReview) : '\u2014',
                           inReview > 0 ? 'with the foundation' : 'no submissions'),
            renderStatTile('Drafts in progress',
                           String(drafts),
                           draftHint),
            renderStatTile('Approval rate',
                           approvalRate !== null ? `${approvalRate}%` : '\u2014',
                           decided > 0 ? `${approvedAll.length} of ${decided}` : 'no decisions yet'),
          ].join('');
        }

        // Ongoing list
        if (ongoingEl) {
          if (ongoing.length === 0) {
            ongoingEl.innerHTML = `
              <div class="card" style="padding: 48px 40px; text-align: center;">
                <p class="t-body" style="margin: 0;">Nothing in progress. When you start a draft it'll appear here.</p>
              </div>`;
          } else {
            ongoingEl.innerHTML = ongoing.map(renderOngoingCard).join('');
          }
        }
        if (ongoingCt) ongoingCt.textContent = `${ongoing.length} active`;

        // History table
        if (historySect && historyEl && historyCt) {
          if (history.length === 0) {
            historySect.style.display = 'none';
          } else {
            historySect.style.display = '';
            historyEl.innerHTML = `
              <table class="ledger">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th style="width: 90px;">Year</th>
                    <th style="width: 160px;">Amount</th>
                    <th style="width: 160px;">Outcome</th>
                    <th style="width: 60px;"></th>
                  </tr>
                </thead>
                <tbody>${history.map(renderHistoryRow).join('')}</tbody>
              </table>
            `;
            historyCt.textContent = `${history.length} ${history.length === 1 ? 'entry' : 'entries'}`;
          }
        }
      })
      .catch(err => {
        if (!alive) return;
        fail((err && err.message) || 'Network error');
      });

    (UserDashboard as Page & { _alive?: () => void })._alive = () => { alive = false; };
  },

  unmount(): void {
    const stop = (UserDashboard as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

// fmtMoneyOrDash is imported so esbuild doesn't tree-shake it on us
// even when this page doesn't reference it directly. (Other migrated
// pages do reference it.) Keeping the import here is harmless.
void fmtMoneyOrDash;
void fmtDate;

export default UserDashboard;
